/**
 * Wire types for the `reports` module — derived field-by-field from
 * refactor/backend/src/modules/reports/routes.ts. There is no serializers.ts
 * for this module: all four endpoints are hand-written `APIView`s returning
 * plain dicts, not `ModelSerializer` output.
 *
 * ALL MONEY FIELDS BELOW ARE PLAIN JSON NUMBERS, not decimal strings — see the
 * file-level comment in routes.ts. The one exception is `RentRoll.upcoming`/
 * `.overdue`, which nest real `InvoiceListRow` rows (imported from
 * types/payment.ts) and so DO carry quantised money STRINGS inside those two
 * arrays specifically.
 */
import type { InvoiceListRow, InvoiceStatus } from './payment';
import type { RequestCategory, RequestPriority, RequestStatus } from './maintenance';

// ---------------------------------------------------------------------------
// GET reports/dashboard/ — role-branched
// ---------------------------------------------------------------------------

/** `DashboardView`'s TENANT branch (routes.ts:161-212). */
export interface TenantDashboard {
  active_lease: {
    unit: string;
    property: string;
    rent_amount: number;
    end_date: string;
  } | null;
  next_payment: {
    amount: number;
    due_date: string;
    status: InvoiceStatus;
  } | null;
  maintenance_requests: {
    total: number;
    new: number;
    in_progress: number;
  };
}

/** `DashboardView`'s manager branch (routes.ts:215-301) — every non-tenant role. */
export interface ManagerDashboard {
  properties: { total: number; pending_approval: number };
  units: { total: number; vacant: number; occupied: number };
  leases: { total: number; active: number; expiring_soon: number };
  payments: { total_due: number; overdue: number; overdue_amount: number };
  maintenance: { total: number; new: number; in_progress: number; urgent: number };
  expenses: { this_month: number };
}

export type Dashboard = TenantDashboard | ManagerDashboard;

/** Narrows `Dashboard` by the field only the tenant shape has. */
export function isTenantDashboard(dashboard: Dashboard): dashboard is TenantDashboard {
  return 'active_lease' in dashboard;
}

// ---------------------------------------------------------------------------
// GET reports/rent-roll/ — RentRollView (routes.ts:309-343)
// ---------------------------------------------------------------------------

export interface RentRoll {
  upcoming: InvoiceListRow[];
  overdue: InvoiceListRow[];
  summary: {
    total_upcoming: number;
    total_overdue: number;
  };
}

// ---------------------------------------------------------------------------
// GET reports/maintenance/ — MaintenanceReportView (routes.ts:349-382)
// ---------------------------------------------------------------------------

export interface MaintenanceReport {
  total_requests: number;
  completed: number;
  by_status: { status: RequestStatus; count: number }[];
  by_category: { category: RequestCategory; count: number }[];
  by_priority: { priority: RequestPriority; count: number }[];
  /** `month` is an ISO-8601 datetime (`TruncMonth` over a DateTimeField). */
  monthly_trend: { month: string; count: number }[];
}

// ---------------------------------------------------------------------------
// GET reports/financial/ — FinancialReportView (routes.ts:388-447)
// ---------------------------------------------------------------------------

export interface FinancialReport {
  total_collected: number;
  total_expenses: number;
  /**
   * Unpaid balance across OPEN/PARTIAL/OVERDUE invoices in scope. NON-PARITY —
   * Django's `FinancialReportView` never returned this; it reuses the same
   * `outstanding()` helper that backs `dashboard.payments.total_due`.
   */
  outstanding_balance: number;
  /** `month` is an ISO-8601 datetime (`TruncMonth('payment_date')`, a DateTimeField). */
  income_trend: { month: string; total: number }[];
  /** `month` is `YYYY-MM-DD` (`TruncMonth('date')` — `Expense.date` is a DateField). */
  expense_trend: { month: string; total: number }[];
  collection_efficiency: number;
}

// ---------------------------------------------------------------------------
// GET reports/tax-summary/ — Node-only, no Django counterpart (routes.ts's
// final section). NOT a tax calculation — an aggregation of rent collected
// minus expenses, per property, for a landlord/agent's own accountant.
// ---------------------------------------------------------------------------

export interface TaxSummaryPropertyRow {
  property_id: number;
  property_name: string;
  gross_rental_income: number;
  deductible_expenses: number;
  net_income: number;
}

export interface TaxSummary {
  year: number;
  /** The caller's own KRA PIN (node_tax_profile), or null if unset — see types/user.ts's `User.kra_pin`. */
  kra_pin: string | null;
  total_gross_rental_income: number;
  total_deductible_expenses: number;
  total_net_income: number;
  by_property: TaxSummaryPropertyRow[];
}
